import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { giamSatThiService } from 'services/giamSatThiService';
import './LoginGiamThi.scss';

export default function LoginGiamThi() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [caThiCode, setCaThiCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPass, setShowPass] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate form
        if (!email || !password) {
            setError('Vui lòng nhập đầy đủ tài khoản và mật khẩu.');
            return;
        }
        if (!caThiCode.trim()) {
            setError('Vui lòng nhập mã ca thi.');
            return;
        }

        setError('');
        setLoading(true);

        try {
            // Gọi API login giám thị: xác thực user + kiểm tra phân công ca thi
            const res: any = await giamSatThiService.login({
                email,
                password,
                caThiCode: caThiCode.trim(),
            });

            const data = res?.data;

            if (data?.status === true) {
                const token = data?.access_token;
                const userId = data?.data?.uid || data?.data?.id;
                const fullname = data?.data?.fullname || email;
                const caThiInfo = data?.ca_thi;

                if (!token) {
                    setError('Không lấy được token. Vui lòng thử lại.');
                    setLoading(false);
                    return;
                }

                // Lưu thông tin đăng nhập
                localStorage.setItem('giam_thi_token', `Bearer ${token}`);
                localStorage.setItem('giam_thi_user_id', String(userId));
                localStorage.setItem('giam_thi_fullname', fullname);
                localStorage.setItem('giam_thi_ca_thi_id', String(caThiInfo?.id));

                // Lưu thông tin ca thi để hiển thị trên dashboard
                if (caThiInfo) {
                    localStorage.setItem('giam_thi_ca_thi_info', JSON.stringify(caThiInfo));
                }

                navigate('/giam-sat-thi');
            } else {
                setError(data?.message || 'Đăng nhập thất bại.');
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-giamthi-container">
            {/* ===== LEFT: FORM ===== */}
            <div className="gt-left-section">
                {/* Brand header */}
                <div className="gt-brand-header">
                    <div className="gt-logo">
                        <img style={{ width: '300px' }} src="/images/logo.jpg" alt="Logo" />
                    </div>
                    <p className="gt-brand-tagline">
                        International English Proficiency Assessment System — <span>IEPAS</span>
                    </p>
                </div>

                {/* Form */}
                <div className="gt-login-wrapper">
                    <h1>Giám Sát Thi.</h1>
                    <p className="gt-subtitle">Đăng nhập hệ thống giám sát dành cho giảng viên</p>

                    <form onSubmit={handleLogin} autoComplete="off">
                        <div className="gt-form-group">
                            <div className="gt-input-icon">
                                <i className="fa-solid fa-user" />
                            </div>
                            <input
                                id="gt-email"
                                type="text"
                                placeholder="Email / Tài khoản"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="off"
                            />
                        </div>

                        <div className="gt-form-group">
                            <div className="gt-input-icon">
                                <i className="fa-solid fa-lock" />
                            </div>
                            <input
                                id="gt-password"
                                type={showPass ? 'text' : 'password'}
                                placeholder="Mật khẩu"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="new-password"
                            />
                            <i
                                className={`fa-solid ${showPass ? 'fa-eye' : 'fa-eye-slash'} gt-password-toggle`}
                                onClick={() => setShowPass((v) => !v)}
                            />
                        </div>

                        <div className="gt-form-group gt-cathi-group">
                            <div className="gt-input-icon">
                                <i className="fa-solid fa-clipboard-list" />
                            </div>
                            <input
                                id="gt-cathi"
                                type="text"
                                placeholder="Mã ca thi (VD: CA01)"
                                value={caThiCode}
                                onChange={(e) => setCaThiCode(e.target.value)}
                                autoComplete="off"
                            />
                            <div className="gt-cathi-hint">
                                <i className="fa-solid fa-circle-question" />
                                <span>Mã ca thi do quản trị viên cung cấp khi phân công giám sát</span>
                            </div>
                        </div>

                        {error && (
                            <div className="gt-error-msg">
                                <i className="fa-solid fa-circle-exclamation" /> {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            className="gt-btn gt-btn-primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="fa-solid fa-spinner fa-spin" /> Đang xác thực...
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-right-to-bracket" /> Vào Giám Sát
                                </>
                            )}
                        </button>
                    </form>

                    <div className="gt-divider" />

                    <div className="gt-info-note">
                        <i className="fa-solid fa-circle-info" />
                        <span>
                            Tài khoản này chỉ dành cho giám thị được phân công.
                            Nhập đúng mã ca thi để vào giám sát. Sau khi đăng nhập, bạn có thể xem tất cả phòng thi trong ca thi đó.
                        </span>
                    </div>
                </div>
            </div>

            {/* ===== RIGHT: IMAGE + OVERLAY ===== */}
            <div className="gt-right-section">
                <div className="gt-right-overlay">
                    <div className="gt-overlay-content">
                        <div className="gt-overlay-icon">
                            <i className="fa-solid fa-user-shield" />
                        </div>
                        <h2>Hệ thống Giám Sát Thi</h2>
                        <p>Theo dõi và quản lý ca thi được phân công theo thời gian thực</p>

                        <div className="gt-flow-steps">
                            <div className="gt-step">
                                <div className="gt-step-num">1</div>
                                <div className="gt-step-text">
                                    <strong>Đăng nhập</strong>
                                    <span>Nhập tài khoản + mã ca thi</span>
                                </div>
                            </div>
                            <div className="gt-step">
                                <div className="gt-step-num">2</div>
                                <div className="gt-step-text">
                                    <strong>Chọn phòng thi</strong>
                                    <span>Xem tất cả hoặc chọn phòng cụ thể</span>
                                </div>
                            </div>
                            <div className="gt-step">
                                <div className="gt-step-num">3</div>
                                <div className="gt-step-text">
                                    <strong>Giám sát real-time</strong>
                                    <span>Theo dõi tiến độ, cảnh báo vi phạm</span>
                                </div>
                            </div>
                        </div>

                        <ul className="gt-features">
                            <li><i className="fa-solid fa-circle-check" /> Theo dõi tiến độ thí sinh</li>
                            <li><i className="fa-solid fa-circle-check" /> Giám sát từng kỹ năng</li>
                            <li><i className="fa-solid fa-circle-check" /> Cảnh báo vi phạm tức thì</li>
                            <li><i className="fa-solid fa-circle-check" /> Khóa / mở lại bài thi</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}
